import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Bell, Mail, MessageSquare, Loader2, X, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Communications() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", channel: "email", recipients: "all_students" });
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/communications`, { withCredentials: true });
      setMessages(data);
    } catch {} finally { setLoading(false); }
  };

  const handleSend = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await axios.post(`${API}/communications`, form, { withCredentials: true });
      setShowModal(false); fetchMessages();
    } catch {} finally { setSending(false); }
  };

  const CHANNEL_ICONS = { email: Mail, sms: MessageSquare, notification: Bell };
  const CHANNEL_COLORS = { email: "#0A7AFF", sms: "#22C55E", notification: "#F59E0B" };

  return (
    <div className="space-y-6" data-testid="communications-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Operations</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Communications</h2>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="compose-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <Send className="w-3.5 h-3.5" /> Compose
        </button>
      </div>

      {/* Channel stats */}
      <div className="grid grid-cols-3 gap-4">
        {[{ label: "Email", icon: Mail, color: "#0A7AFF", count: messages.filter(m => m.channel === "email").length },
          { label: "SMS", icon: MessageSquare, color: "#22C55E", count: messages.filter(m => m.channel === "sms").length },
          { label: "Notifications", icon: Bell, color: "#F59E0B", count: messages.filter(m => m.channel === "notification").length }
        ].map((c, i) => (
          <div key={i} className="bg-white border border-[#E2DDD4] p-5 flex items-center gap-4" style={{ borderLeft: `3px solid ${c.color}` }}>
            <div className="w-8 h-8 flex items-center justify-center" style={{ background: c.color + "15" }}>
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
            </div>
            <div>
              <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8]">{c.label}</p>
              <p className="font-['JetBrains_Mono'] font-bold text-2xl text-[#0C0C10]">{c.count}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Recent messages */}
      <div className="bg-white border border-[#E2DDD4]">
        <div className="px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7] grid" style={{ gridTemplateColumns: "40px 1.5fr 1fr 100px 100px" }}>
          {["", "Subject", "Recipients", "Channel", "Sent"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          : messages.length === 0 ? <div className="py-12 text-center font-['DM_Sans'] text-sm text-[#BDBAA8]">No messages sent yet</div>
          : messages.map((msg, i) => {
            const Icon = CHANNEL_ICONS[msg.channel] || Bell;
            const color = CHANNEL_COLORS[msg.channel] || "#BDBAA8";
            return (
              <div key={msg._id} className="grid items-center px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7]" style={{ gridTemplateColumns: "40px 1.5fr 1fr 100px 100px" }}>
                <div className="w-6 h-6 flex items-center justify-center" style={{ background: color + "15" }}><Icon className="w-3.5 h-3.5" style={{ color }} /></div>
                <div>
                  <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{msg.subject}</p>
                  <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] line-clamp-1">{msg.body}</p>
                </div>
                <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{msg.recipients?.replace(/_/g, " ")}</span>
                <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider px-2 py-1 border w-fit" style={{ color, borderColor: color + "40", background: color + "10" }}>{msg.channel}</span>
                <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{new Date(msg.sent_at).toLocaleDateString("en-IN")}</span>
              </div>
            );
          })}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-lg w-full p-8">
              <div className="flex justify-between mb-6"><h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Compose Message</h3><button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button></div>
              <form onSubmit={handleSend} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Channel</label>
                  <select value={form.channel} onChange={e => setForm(p => ({...p, channel: e.target.value}))}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                    <option value="email">Email</option><option value="sms">SMS</option><option value="notification">Notification</option>
                  </select></div>
                  <div><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Recipients</label>
                  <select value={form.recipients} onChange={e => setForm(p => ({...p, recipients: e.target.value}))}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                    <option value="all_students">All Students</option><option value="all_faculty">All Faculty</option><option value="all">Everyone</option>
                  </select></div>
                </div>
                <div><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Subject</label>
                <input value={form.subject} onChange={e => setForm(p => ({...p, subject: e.target.value}))} required
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" /></div>
                <div><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Message</label>
                <textarea value={form.body} onChange={e => setForm(p => ({...p, body: e.target.value}))} required rows={4}
                  className="w-full bg-transparent border border-[#D8D4CB] p-3 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors resize-none" /></div>
                <button type="submit" disabled={sending} className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send</>}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
